/**
 * M14 — memory meter. Remembered slots used against the limit, or the
 * people f8 stopped remembering. Card with the meter bar, never dismissable.
 * The tap is `open` (the remembered list).
 *
 * "paused" ≠ "never remembered": a paused person's notes are safe and f8
 * has stopped adding to them; a never-remembered person was never in a slot.
 * Adding a contact never spends a slot. The limit is a payload number, never
 * part of the string. Premium users never see this module (the server does
 * not send it).
 */

import { HomeCard, HomeChevron } from '../HomeKit';
import { joinNames } from '../format';
import type { ModuleProps } from '../moduleTypes';

export function MemoryModule({ module, onAction }: ModuleProps) {
  if (module.type !== 'memory') return null;
  const { payload } = module;
  const limit = Math.max(0, payload.limit);
  const pct = limit > 0 ? Math.round(Math.min(1, payload.count / limit) * 100) : 100;

  let body: string;
  let sub: string;
  if (payload.variant === 'paused' && payload.paused.length) {
    body = `f8 paused remembering ${joinNames(payload.paused.map((p) => p.personName))}`;
    sub = 'their notes are safe — pick who f8 remembers';
  } else if (payload.variant === 'atCap') {
    body = 'All your remembered slots are used';
    sub = `${payload.count} of ${limit} · pick who f8 remembers`;
  } else {
    body = 'Nearly out of remembered slots';
    sub = `${payload.count} of ${limit} slots used`;
  }

  return (
    <HomeCard
      reason={module.reason}
      body={body}
      sub={sub}
      right={<HomeChevron />}
      dismissable={false}
      onOpen={() => onAction?.({ kind: 'open' }, module)}
    >
      <div className="home-meter" role="meter" aria-valuemin={0} aria-valuemax={limit} aria-valuenow={Math.min(payload.count, limit)} aria-label="remembered slots used">
        <div className="home-meter-fill" style={{ width: `${pct}%` }} />
      </div>
    </HomeCard>
  );
}
